import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Button,
  FeedOutlinedIcon,
  SettingsOutlinedIcon,
  Stack,
  Typography,
  CircularProgress
} from '@esgian/esgianui';
import { createRigFuelPrice, deleteFuelPrice, updateRigFuelPrice } from '@api/Settings';
import moment from 'moment-timezone';
import Swal from 'sweetalert2';
import { useTheme } from '@hooks';
import { useDispatch, useSelector } from 'react-redux';
import { RigFuelPriceModal } from '@components/Modals';
import { TIME_ZONE, DATE_FORMAT, DATE_TIME_FORMAT, CUSTOM_TIMEZONE_FORMAT } from '@constants/index';
import {
  getMainPageRig,
  getMainPageSlice,
  setTimeZone,
  setCustomUTC,
  getCustomUTC,
  getTimeZone,
  getRigUTC
} from '@store/features/filters/MainPageSlice/MainPageSlice';
import { getLookupRigs } from '@store/features';
import { useMainPageQueries } from '@hooks/usePageQueries/useMainPageQueries';
import { useTimezone } from '@hooks/useTimezone';
import CreateReportModal from '@components/Modals/ReportScheduler/CreateReportModal';
import { sendAndDownloadReport } from '@api/ReportScheduler';
import { logError } from '@api/Logging';
import PropTypes from 'prop-types';

function PageActionButtons({
  isReportLoading,
  setReportIsLoading
}
) {
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const dispatch = useDispatch();
  const selectedRig = useSelector(getMainPageRig);
  const { startDate, endDate } = useSelector(getMainPageSlice);
  const lookupRigs = useSelector(getLookupRigs);
  const selectedUTC = useSelector(getCustomUTC);
  const timeZone = useSelector(getTimeZone);
  const rigUTC = useSelector(getRigUTC);
  const { theme } = useTheme();
  const { resetMainPageData } = useMainPageQueries();
  const { selectedTimeZone } = useTimezone();
  const [exportModalOpen, setExportModalOpen] = useState(false);

  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    const timerId = setInterval(() => {
      setCurrentDate(new Date());
    }, 1000);

    return () => clearInterval(timerId);
  }, []);

  const handleSaveSettings = async (
    deleteItems,
    tempFuelPrices,
    fuelPrices,
    selectedTimeZone,
    selectedCustomUTC
  ) => {
    Swal.fire({
      title: 'Saving settings...',
      background: theme.palette.neutral.neutral01,
      color: theme.palette.text.primary
    });
    Swal.showLoading();

    dispatch(setTimeZone(selectedTimeZone));
    dispatch(setCustomUTC(selectedCustomUTC));

    const controller = new AbortController();
    const { signal } = controller;
    setSettingsModalOpen(false);
    let apiCalls = [];
    deleteItems.forEach((item) => {
      apiCalls.push(deleteFuelPrice(signal, selectedRig, item));
    });
    let newItemsCount = 0;
    tempFuelPrices.forEach((item, i) => {
      if (item.isNew) {
        newItemsCount++;
      }
      if (item.isNew && item.price && moment(item.startDate).isValid()) {
        apiCalls.push(
          createRigFuelPrice(signal, selectedRig, { ...item, rigId: selectedRig.rigId })
        );
      }
      if (!item.isNew && item.price !== fuelPrices[i - newItemsCount].price) {
        apiCalls.push(updateRigFuelPrice(signal, selectedRig, item));
      }
    });
    if (apiCalls.length === 0) {
      Swal.close();
    }
    try {
      await Promise.all(apiCalls)
        .then(() => {
          Swal.close();
          resetMainPageData();
          Swal.fire({
            position: 'center',
            icon: 'success',
            title: 'Settings saved',
            background: theme.palette.neutral.neutral01,
            color: theme.palette.text.primary,
            showConfirmButton: false,
            timer: 1500
          });
        })
        .catch(() => {
          Swal.fire({
            position: 'center',
            icon: 'error',
            background: theme.palette.neutral.neutral01,
            color: theme.palette.text.primary,
            title: 'Unable to save settings',
            showConfirmButton: false,
            timer: 1500
          }).then(() => {
            setSettingsModalOpen(true);
          });
        });
    } catch (e) {
      setSettingsModalOpen(true);
    }
  };

  const selectedCustomUTC = useMemo(() => {
    // if costum time zone is set
    if (timeZone === TIME_ZONE.CUSTOM_TIME_ZONE && selectedUTC?.value) {
      const timezone = selectedUTC?.value || null;
      const momentCustomDateTime = moment().tz(timezone);
      const formattedCustomTime = momentCustomDateTime.format(CUSTOM_TIMEZONE_FORMAT);
      return `CUSTOM TIME ZONE: ${formattedCustomTime}`;
    }
    // if costum time zone is NOT set - time is displayed based on rigName selection
    if (timeZone === TIME_ZONE.RIGS_LOCATION || !selectedUTC?.value) {
      const momentDefaultDateTime = moment().tz(rigUTC || 'Europe/Oslo');
      const formattedDefaultTime = momentDefaultDateTime.format(CUSTOM_TIMEZONE_FORMAT);
      return `CURRENT RIG TIME: ${formattedDefaultTime}`;
    }
    return '';
  }, [currentDate, selectedUTC, rigUTC]);

  const reportInitialData = useMemo(() => {
    if (!selectedRig) return null;
    const rigName = selectedRig.rigName || '';
    const periodDates = `${moment(startDate).format(DATE_FORMAT)} - ${moment(endDate).format(DATE_FORMAT)}`;
    return {
      rigId: selectedRig.rigId,
      reportType: 'RigOverview',
      period: 'Custom',
      periodStartDate: moment.tz(startDate, DATE_FORMAT, selectedTimeZone).startOf('day').format(DATE_TIME_FORMAT),
      periodEndDate: moment.tz(endDate, DATE_FORMAT, selectedTimeZone).endOf('day').format(DATE_TIME_FORMAT),
      reportName: `${rigName} - RigOverview - ${periodDates}`,
      isImmediate: true,
      visibility: 'user',
      options: { showTrend: true }
    };
  }, [selectedRig, startDate, endDate, selectedTimeZone]);

  const handleSendNow = useCallback((body) => {
    if (body.downloadImmediate) {
      setReportIsLoading(true);
      sendAndDownloadReport(null, body)
        .then(() => {
          setReportIsLoading(false);
        })
        .catch((error) => {
          logError(error, 'Failed to send and download report');
          setReportIsLoading(false);
        });
    }
  }, [setReportIsLoading]);

  return (
    <Stack direction={'row'} spacing={1} sx={{ alignItems: 'center' }}>
      <Typography sx={{ padding: '8px 16px', fontSize: '14px' }}>{selectedCustomUTC}</Typography>
      <Button
        variant={'text'}
        color={'secondary'}
        sx={{ color: (theme) => theme.palette.secondary.contrastText }}
        onClick={() => {
          setSettingsModalOpen(true);
        }}
        startIcon={<SettingsOutlinedIcon color={'inherit'} />}>
        Settings
      </Button>
      <Button
        variant={'text'}
        // disabled={isReportLoading}
        color={'secondary'}
        sx={{ color: (theme) => theme.palette.secondary.contrastText }}
        onClick={() => {
          setExportModalOpen(true);
        }}
        startIcon={isReportLoading ? <CircularProgress size={10} /> : <FeedOutlinedIcon color={'inherit'} />}>
        report
      </Button>
      {settingsModalOpen && (
        <RigFuelPriceModal
          handleSave={handleSaveSettings}
          handleClose={() => {
            setSettingsModalOpen(false);
          }}
        />
      )}
      {exportModalOpen && (
        <CreateReportModal
          open={exportModalOpen}
          handleClose={() => { setExportModalOpen(false); }}
          lookupRigs={lookupRigs}
          initialData={reportInitialData}
          onSendNow={handleSendNow}
        />
      )}
    </Stack>
  );
}

PageActionButtons.propTypes = {
  isReportLoading: PropTypes.bool.isRequired,
  setReportIsLoading: PropTypes.func.isRequired
};

PageActionButtons.defaultProps = {};

export default PageActionButtons;
